import { validate as uuidValidate } from 'uuid';

/**
 * A project is addressed as the leaf of the folder path it lives in, e.g.
 * `/folders/TeamA/Bridges/9f3c…`. The folder segments are context — they make the URL
 * readable and let the breadcrumb derive itself the same way it does for folders — while
 * the trailing uuid is the authority. Project names are not unique within a folder
 * (only the manifest id is), so the id is what resolves the URL; a stale folder prefix
 * left behind by a move still opens the right project and gets rewritten in place.
 *
 * React Router splat params must be terminal, so a variable-length folder prefix cannot
 * be expressed as a route pattern. Both families are therefore matched as a single splat
 * and parsed here — this module is the only place that reads or writes these URLs.
 */

export const FOLDER_BASE = '/folders';
export const VIEWER_BASE = '/viewer/folders';

const PROJECT_BASES = [VIEWER_BASE, FOLDER_BASE] as const;

export type ProjectBase = typeof PROJECT_BASES[number];

export type ProjectTab = 'overview' | 'inspections' | 'checklists';

export interface ProjectLocation {
  base: ProjectBase;
  /** Decoded folder path (`/A/B`), empty when the project sits outside any folder. */
  folderPath: string;
  /** Absent while the URL still points at a folder rather than a project. */
  projectId?: string;
  tab: ProjectTab;
  inspectionId?: string;
  poiId?: string;
  stepIndex?: string;
}

export interface ProjectPathParams {
  base?: ProjectBase;
  folderPath?: string;
  projectId: string;
  tab?: ProjectTab;
  inspectionId?: string;
  poiId?: string;
  stepIndex?: number | string;
}

const encodeSegments = (path: string): string =>
  path.split('/').filter(Boolean).map(encodeURIComponent).join('/');

const decodeSegments = (path: string): string[] =>
  path.split('/').filter(Boolean).map(decodeURIComponent);

/** Folder listing URL: `/A/B` -> `/folders/A/B`. */
export const buildFolderPath = (folderPath: string): string => {
  const encoded = encodeSegments(folderPath);

  return encoded ? `${FOLDER_BASE}/${encoded}` : FOLDER_BASE;
};

export const buildProjectPath = (
  { base = FOLDER_BASE, folderPath = '', projectId, tab, inspectionId, poiId, stepIndex }: ProjectPathParams
): string => {
  const segments = [encodeSegments(folderPath), projectId].filter(Boolean);

  // An inspection selection implies the inspections tab, so the two never both contribute.
  if (inspectionId) {
    segments.push('inspections', inspectionId);

    if (poiId) {
      segments.push('poi', poiId);
      if (stepIndex !== undefined) segments.push('steps', String(stepIndex));
    }
  } else if (tab && tab !== 'overview') {
    segments.push(tab);
  }

  return `${base}/${segments.join('/')}`;
};

const parseTail = (tail: string[]): Pick<ProjectLocation, 'tab' | 'inspectionId' | 'poiId' | 'stepIndex'> => {
  const [head, inspectionId, poiKey, poiId, stepKey, stepIndex] = tail;

  if (head === 'checklists') return { tab: 'checklists' };
  if (head !== 'inspections') return { tab: 'overview' };

  return {
    tab: 'inspections',
    inspectionId,
    poiId: poiKey === 'poi' ? poiId : undefined,
    stepIndex: stepKey === 'steps' ? stepIndex : undefined,
  };
};

/**
 * Splits a pathname into its folder prefix, project id and tail. Returns null for any
 * URL outside the two project families, which is how callers tell "not a project page"
 * apart from "a folder page with no project selected".
 */
export const parseProjectLocation = (pathname: string): ProjectLocation | null => {
  const base = PROJECT_BASES.find((candidate) => pathname === candidate || pathname.startsWith(`${candidate}/`));

  if (!base) return null;

  const segments = decodeSegments(pathname.slice(base.length));
  // The first uuid-shaped segment ends the folder path: folder names are free text and a
  // project id is always a uuid. A folder named like a uuid would be misread as a project.
  const idIndex = segments.findIndex((segment) => uuidValidate(segment));

  const folderSegments = idIndex === -1 ? segments : segments.slice(0, idIndex);
  const folderPath = folderSegments.length ? `/${folderSegments.join('/')}` : '';

  if (idIndex === -1) return { base, folderPath, tab: 'overview' };

  const [projectId, ...tail] = segments.slice(idIndex);

  return { base, folderPath, projectId, ...parseTail(tail) };
};
